import React from "react";
import { Link } from "react-router-dom";
import { HeartPulse, Users, ShieldCheck, Fish, Mic, BadgeCheck } from "lucide-react";
import { Reveal, Stagger, StaggerItem, ParallaxImage } from "@/lib/motion";
import { IMG, EMBLEM_FAMILY } from "@/data/images";
import { useImg } from "@/lib/useImg";
import { useAdaptiveHero } from "@/lib/useAdaptiveHero";

const SADHANA = {
  eyebrow: "Founder",
  name: "Dr. Sadhana Dash",
  role: "Public Health Professional",
  sub: "Former Chief District Medical & Public Health Officer (CDMO)",
  image: IMG.founderSadhana,
  quote: "Health is not only about treating disease — it begins much earlier, with prevention, nutrition and the everyday choices we make.",
  bio: [
    "With a distinguished career in public health, Dr. Sadhana Dash retired after serving as Chief District Medical & Public Health Officer (CDMO), a position entrusted with the responsibility of overseeing and strengthening healthcare and public-health initiatives at the district level.",
    "Her years of service gave her a deep understanding of community health, preventive care and the challenges people face in maintaining good health. This experience shaped her belief that lasting wellness begins with nutrition, awareness and safety.",
    "At Nutribest, Dr. Sadhana Dash brings her expertise in public health, nutritional awareness and quality assurance — ensuring that every product we create supports the health and wellbeing of families.",
  ],
  highlights: [
    { icon: HeartPulse, text: "Retired as CDMO (Chief District Medical & Public Health Officer)" },
    { icon: Users, text: "Decades of experience in community health and public health programs" },
    { icon: ShieldCheck, text: "Deep commitment to preventive healthcare, nutrition and community wellbeing" },
  ],
};

const SUJATA = {
  eyebrow: "Founder",
  name: "Smt. Sujata Bhuyan",
  role: "Agri-Entrepreneur & Fisheries Leader",
  sub: "",
  image: IMG.founderSujata,
  quote: "Real success is when your work creates opportunity for others — especially for farmers and women in your community.",
  bio: [
    "From a homemaker to a national role model, Smt. Sujata Bhuyan's journey is a testament to determination, innovation and the power of rural enterprise. She built a successful reservoir cage-culture enterprise in the Hirakud Reservoir of Sambalpur, working closely with farmers, scientists and institutional support.",
    "Her journey received national recognition when Hon'ble Prime Minister Shri Narendra Modi ji highlighted her story in his Mann Ki Baat address, acknowledging her transformation and inspiring countless women across the country.",
    "Her work has also been documented by ICAR, as an example of how science, entrepreneurship and determination can create sustainable rural livelihoods.",
    "At Nutribest, Sujata brings her deep understanding of agriculture, farmers, natural resources and rural enterprise — ensuring that our superfoods are sourced responsibly and support the farming communities who make it possible.",
  ],
  highlights: [
    { icon: Fish, text: "Successful reservoir cage-culture entrepreneur in Hirakud Reservoir, Sambalpur" },
    { icon: Mic, text: "Recognised by Hon'ble Prime Minister Shri Narendra Modi ji in Mann Ki Baat" },
    { icon: BadgeCheck, text: "Documented by ICAR as an example of successful rural enterprise and women empowerment" },
  ],
};

const Highlights = ({ items }) => (
  <div data-testid="founder-highlights" className="bg-creamdark/50 border border-forest/10 rounded-2xl p-6">
    <p className="eyebrow !text-forest/60 mb-5">Key Highlights</p>
    <Stagger className="space-y-5">
      {items.map(({ icon: Icon, text }) => (
        <StaggerItem key={text}>
          <div className="flex gap-3.5">
            <span className="shrink-0 w-9 h-9 rounded-full bg-white border border-gold/40 flex items-center justify-center text-forest">
              <Icon size={16} strokeWidth={1.7} />
            </span>
            <p className="text-xs sm:text-[13px] text-mutedgreen leading-relaxed pt-1.5">{text}</p>
          </div>
        </StaggerItem>
      ))}
    </Stagger>
  </div>
);

const Portrait = ({ src, name, quote }) => (
  <Reveal className="flex flex-col gap-6">
    <div className="overflow-hidden rounded-2xl shadow-xl shadow-forest/10">
      <img src={src} alt={name} className="w-full h-[420px] object-cover object-top" />
    </div>
    <blockquote className="border-l-2 border-gold pl-5">
      <p className="font-serif text-lg sm:text-xl text-forest italic leading-snug">"{quote}"</p>
      <footer className="mt-3 text-xs font-bold uppercase tracking-[0.16em] text-forest/70">— {name}</footer>
    </blockquote>
  </Reveal>
);

const Bio = ({ data }) => (
  <Reveal delay={0.1}>
    <p className="eyebrow mb-3">{data.eyebrow}</p>
    <h3 className="font-serif text-3xl sm:text-4xl text-forest leading-tight">{data.name}</h3>
    <p className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-gold">{data.role}</p>
    {data.sub && <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest/60">{data.sub}</p>}
    <div className="mt-6 space-y-4 text-sm text-mutedgreen leading-relaxed">
      {data.bio.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
    </div>
  </Reveal>
);

const FounderProfile = ({ data, reversed = false, testid }) => (
  <section data-testid={testid} className={`py-16 sm:py-20 ${reversed ? "bg-creamdark/30 border-y border-forest/10" : ""}`}>
    <div className="container-nb grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
      <div className={`lg:col-span-4 ${reversed ? "lg:order-3" : "lg:order-1"}`}>
        <Portrait src={data.image} name={data.name} quote={data.quote} />
      </div>
      <div className="lg:col-span-5 lg:order-2">
        <Bio data={data} />
      </div>
      <div className={`lg:col-span-3 ${reversed ? "lg:order-1" : "lg:order-3"}`}>
        <Reveal delay={0.2}><Highlights items={data.highlights} /></Reveal>
      </div>
    </div>
  </section>
);

export const FoundingLeaders = () => {
  const img = useImg();
  const sadhana = { ...SADHANA, image: img("founderSadhana") };
  const sujata = { ...SUJATA, image: img("founderSujata") };
  const heroImg = img("foundersHero");
  const { ref: heroRef, height: heroH } = useAdaptiveHero(heroImg, { mobileMinVh: 74, desktopMinVh: 66 });
  return (
  <div data-testid="founding-leaders">
    <section ref={heroRef} className={`relative ${heroH ? "" : "min-h-[74vh]"} flex items-end sm:items-center overflow-hidden bg-forestdeep`} style={heroH ? { height: `${heroH}px` } : undefined}>
      <div className="absolute inset-0">
        <img src={heroImg} alt="" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-forestdeep/80 via-forestdeep/35 to-forestdeep/15" />
        <div className="absolute inset-y-0 left-0 w-full lg:w-2/3 bg-gradient-to-r from-forestdeep/60 via-forestdeep/20 to-transparent" />
      </div>
      <div className="relative container-nb py-16 sm:py-20 [text-shadow:0_2px_22px_rgba(0,0,0,0.55)]">
        <Reveal>
          <p className="eyebrow !text-[#F2D27C] !font-bold mb-5 inline-flex items-center gap-3 before:content-[''] before:h-px before:w-8 before:bg-[#F2D27C]">Founding Leaders</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream leading-[1.08] tracking-tight max-w-3xl">The Convergence<br />of Health and Harvest</h2>
          <p className="mt-6 text-cream text-base sm:text-lg max-w-xl leading-relaxed">Two accomplished women. Two unique journeys. One shared purpose — to bring the goodness of nature closer to everyday health.</p>
        </Reveal>
      </div>
    </section>

    <FounderProfile data={sadhana} testid="founder-sadhana" />
    <FounderProfile data={sujata} reversed testid="founder-sujata" />

    <section className="relative py-20 sm:py-28 overflow-hidden">
      <ParallaxImage src={img("twoJourneys")} alt="" className="absolute inset-0" speed={12} />
      <div className="absolute inset-0 bg-forestdeep/75" />
      <div className="relative container-nb grid lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-14 items-center">
        <Reveal className="[text-shadow:0_2px_18px_rgba(0,0,0,0.5)]">
          <p className="eyebrow !text-goldlight mb-4">When Two Journeys Came Together</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-[2.7rem] text-cream leading-[1.15]">Health and Harvest. A Shared Purpose.</h2>
          <div className="mt-6 space-y-4 text-sm sm:text-[15px] text-cream/85 leading-relaxed max-w-2xl">
            <p>When Dr. Sadhana Dash and Smt. Sujata Bhuyan crossed paths in Deogarh, their experiences came together around a common idea.</p>
            <p>Dr. Dash brought the perspective of health, public wellbeing and nutritional responsibility. Sujata brought the perspective of agriculture, entrepreneurship and the value of natural resources.</p>
            <p>They recognised that these two worlds could complement each other — that the journey from healthy soil to healthy food, and from healthy food to healthier families, deserved greater attention.</p>
            <p className="font-serif text-lg text-goldlight italic">That realisation became the foundation of Nutribest Superfoods Pvt. Ltd.</p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="bg-cream/10 border border-cream/20 rounded-2xl p-8 backdrop-blur-sm text-center">
            <img src={EMBLEM_FAMILY} alt="Nutribest family seal" className="w-24 h-24 mx-auto object-contain drop-shadow" />
            <p className="mt-5 font-serif text-xl sm:text-2xl text-cream leading-snug italic">"If it isn't good enough for our own family, it isn't good enough for our customers."</p>
          </div>
        </Reveal>
      </div>
    </section>

    <section data-testid="our-vision" className="py-20 sm:py-28 bg-cream">
      <div className="container-nb grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <p className="eyebrow mb-4 inline-flex items-center gap-3 before:content-[''] before:h-px before:w-8 before:bg-gold">Our Vision</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-[2.7rem] text-forest leading-[1.15] tracking-tight">To make pure, nutrient-rich superfoods a trusted part of everyday health.</h2>
          <p className="mt-6 text-sm sm:text-base text-mutedgreen leading-relaxed max-w-xl">We believe India's natural resources and traditional superfoods have enormous potential when combined with responsible agriculture, modern processing, scientific understanding and uncompromising quality standards.</p>
          <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-forest/15 bg-white px-2 py-2">
            <Link to="/collection" data-testid="vision-pure-nutrition-link" className="rounded-full bg-forest px-5 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-cream hover:bg-gold transition-colors duration-300">Pure Nutrition</Link>
            <Link to="/experience" data-testid="vision-real-superfoods-link" className="rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-forest hover:text-gold transition-colors duration-300">Real Superfoods</Link>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative">
            <div className="overflow-hidden rounded-3xl shadow-xl shadow-forest/10">
              <img src={img("soilHands")} alt="Hands nurturing a young seedling" className="w-full h-[360px] sm:h-[440px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-4 sm:-left-6 bg-forestdeep grain rounded-2xl px-7 py-6 shadow-lg max-w-[240px]">
              <img src={EMBLEM_FAMILY} alt="" className="w-11 h-11 object-contain" />
              <p className="mt-3 font-serif text-lg text-cream leading-snug">From healthy soil to healthier families.</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  </div>
  );
};
